/**
 * 포스트프로세싱 (Phase A 화질 업그레이드).
 * RenderPass → GTAO(앰비언트 오클루전) → Bloom → ColorGrade(비네트 병합) → SMAA → Output.
 * SSAO 대신 상위 품질의 GTAO 사용. `?nofx` 완전 비활성, `?noao` AO만 비활성.
 */
import * as THREE from 'three';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';
import { ShaderPass } from 'three/examples/jsm/postprocessing/ShaderPass.js';
import { GTAOPass } from 'three/examples/jsm/postprocessing/GTAOPass.js';
import { SMAAPass } from 'three/examples/jsm/postprocessing/SMAAPass.js';
import { OutputPass } from 'three/examples/jsm/postprocessing/OutputPass.js';

/**
 * 컬러 그레이딩 + 비네트: 대비·채도·톤 조정으로 "게임 컷" 느낌. 비네트는 별도
 * ShaderPass(three VignetteShader) 대신 여기 마지막 단계에 동일 수식으로 합성 —
 * 디바이스 해상도 풀스크린 read/write 패스 1회를 아낀다(픽셀 출력은 2패스와 동일:
 * 기존에도 Grade(클램프) → Vignette 순서였다).
 */
const GradeShader = {
  uniforms: {
    tDiffuse: { value: null as THREE.Texture | null },
    contrast: { value: 1.04 },
    saturation: { value: 1.03 },
    brightness: { value: 0.01 },
    warmth: { value: 0.008 },
    vignetteOffset: { value: 1.1 },
    vignetteDarkness: { value: 0.72 },
  },
  vertexShader: /* glsl */ `
    varying vec2 vUv;
    void main() { vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }
  `,
  fragmentShader: /* glsl */ `
    uniform sampler2D tDiffuse;
    uniform float contrast, saturation, brightness, warmth;
    uniform float vignetteOffset, vignetteDarkness;
    varying vec2 vUv;
    void main() {
      vec4 c = texture2D(tDiffuse, vUv);
      c.rgb += brightness;
      c.rgb = (c.rgb - 0.5) * contrast + 0.5;
      float l = dot(c.rgb, vec3(0.2126, 0.7152, 0.0722));
      c.rgb = mix(vec3(l), c.rgb, saturation);
      c.r += warmth; c.b -= warmth * 0.6;
      c.rgb = clamp(c.rgb, 0.0, 1.0);
      // 비네트 — three VignetteShader(Eskil 식)와 동일 수식.
      vec2 v = (vUv - vec2(0.5)) * vec2(vignetteOffset);
      gl_FragColor = vec4(mix(c.rgb, vec3(1.0 - vignetteDarkness), dot(v, v)), c.a);
    }
  `,
};

export class PostFX {
  private composer: EffectComposer;
  private gtao: GTAOPass | null = null;
  readonly enabled: boolean;

  constructor(
    private renderer: THREE.WebGLRenderer,
    scene: THREE.Scene,
    camera: THREE.PerspectiveCamera,
  ) {
    const params = new URLSearchParams(location.search);
    this.enabled = !params.has('nofx');
    // 리얼리즘: GTAO 앰비언트 오클루전 기본 활성(접지감). `?noao`로 비활성.
    const useAO = !params.has('noao');
    const w = window.innerWidth;
    const h = window.innerHeight;

    this.composer = new EffectComposer(renderer);
    this.composer.addPass(new RenderPass(scene, camera));

    if (useAO) {
      const gtao = new GTAOPass(scene, camera, w, h);
      gtao.output = GTAOPass.OUTPUT.Default;
      // 씬 스케일(오브젝트 0.1~1 유닛)에 맞춘 좁은 AO 반경.
      gtao.updateGtaoMaterial({ radius: 0.18, distanceExponent: 1.0, thickness: 1.0, scale: 1.0, samples: 16 });
      gtao.blendIntensity = 0.9;
      this.composer.addPass(gtao);
      this.gtao = gtao;
    }

    // 리얼리즘: 은은한 블룸(밝은 형광패널만), 약한 비네트(Grade 패스에 병합).
    const bloom = new UnrealBloomPass(new THREE.Vector2(w, h), 0.18, 0.6, 0.95);
    this.composer.addPass(bloom);

    const grade = new ShaderPass(GradeShader);
    this.composer.addPass(grade);

    const smaa = new SMAAPass(w, h);
    this.composer.addPass(smaa);

    this.composer.addPass(new OutputPass());
    this.resize();
  }

  resize(): void {
    const w = window.innerWidth;
    const h = window.innerHeight;
    const pr = Math.min(window.devicePixelRatio, 2);
    this.composer.setSize(w, h);
    this.composer.setPixelRatio(pr);
    // GTAO는 절반 해상도로 강등: AO 자체 + 내부 depth/normal 프리패스(씬 전체 재렌더)의
    // 픽셀 비용이 1/4로 줄어든다. DPR 2 모바일에서 프레임 예산(33ms)을 지키는 핵심 티어링.
    // composer.setSize/setPixelRatio가 모든 패스를 풀해상도로 되돌리므로 반드시 그 뒤에 호출.
    this.gtao?.setSize(Math.max(1, Math.round((w * pr) / 2)), Math.max(1, Math.round((h * pr) / 2)));
  }

  render(scene: THREE.Scene, camera: THREE.Camera): void {
    if (this.enabled) {
      this.composer.render();
    } else {
      this.renderer.render(scene, camera);
    }
  }
}
